import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlatformApiError,
  type JournalDraft,
  type JournalPost,
  type JournalRevision,
  type PublicJournalPost,
  type Viewer,
} from '@sfc/sdk';
import { platform } from '../api/client';
import { Empty } from '../ui/Empty';
import { dateTime, relativeTime } from '../ui/format';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';
import { Table, type Column } from '../ui/Table';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  unpublished: 'Taken down',
  archived: 'Archived',
};

const STATUS_TONES: Record<string, string> = {
  draft: 'pill--pending',
  published: 'pill--up',
  unpublished: 'pill--down',
  archived: 'pill--pending',
};

/**
 * Articles, and the editor that writes them.
 *
 * The list is what an editor sees first: every article in whatever state, with
 * the two questions the dashboard has to answer at a glance -- what readers are
 * being served, and what somebody is still working on. Publishing is a decision
 * about a *revision*, so the editor keeps the working draft and the published
 * words visibly apart.
 */
export function JournalPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const mayWrite = viewer.capabilities.includes('journal:write');

  const [articles, setArticles] = useState<JournalPost[]>();
  const [status, setStatus] = useState('');
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<JournalDraft | 'new' | undefined>();

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setBusy(true);
      setProblem(undefined);
      try {
        setArticles(await platform.journal.list(organizationId, signal));
      } catch (error) {
        if (!signal?.aborted) setProblem(describe(error));
      } finally {
        if (!signal?.aborted) setBusy(false);
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Before the editor branch below, so the number of hooks is the same whether
  // the list or the editor is on screen.
  const stats = useMemo(() => {
    const rows = articles ?? [];
    const count = (state: string) => rows.filter((article) => article.status === state).length;
    return [
      { label: 'Articles', value: rows.length },
      { label: 'Readers see', value: count('published'), tone: count('published') > 0 ? ('up' as const) : undefined },
      { label: 'Drafts', value: count('draft') },
      { label: 'Taken down', value: count('unpublished'), tone: count('unpublished') > 0 ? ('down' as const) : undefined },
    ];
  }, [articles]);

  if (editing) {
    return (
      <ArticleEditor
        viewer={viewer}
        draft={editing}
        onClose={() => {
          setEditing(undefined);
          void load();
        }}
        onChanged={(draft) => setEditing(draft)}
      />
    );
  }

  const shown = (articles ?? []).filter((article) => !status || article.status === status);

  const columns: Column<JournalPost>[] = [
    {
      key: 'title',
      header: 'Article',
      render: (article) => (
        <span className="cell-stack">
          <span className="row__title">{article.title}</span>
          <code className="slug">/journal/{article.slug}</code>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (article) => (
        <span
          className={`pill ${STATUS_TONES[article.status] ?? 'pill--pending'}`}
          data-testid={`article-status-${article.slug}`}
        >
          {STATUS_LABELS[article.status] ?? article.status}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (article) => (
        <time className="row__sub" dateTime={article.updatedAt} title={dateTime(article.updatedAt)}>
          {relativeTime(article.updatedAt)}
        </time>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (article) => (
        <button
          className="button button--small"
          aria-label={`Open ${article.title}`}
          data-testid={`open-article-${article.slug}`}
          onClick={() => void open(article.id)}
        >
          Open
        </button>
      ),
    },
  ];

  async function open(id: string): Promise<void> {
    setProblem(undefined);
    try {
      setEditing(await platform.journal.read(organizationId, id));
    } catch (error) {
      setProblem(describe(error));
    }
  }

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="toolbar">
        <select
          className="select"
          aria-label="Filter by status"
          data-testid="article-status-filter"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">All states</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="toolbar__spacer" />
        <button className="button" disabled={busy} onClick={() => void load()}>
          <Icon name="refresh" size={16} />
          Refresh
        </button>
        {mayWrite ? (
          <button className="button button--primary" data-testid="new-article" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} />
            New article
          </button>
        ) : null}
      </div>

      <StatStrip stats={stats} testId="journal-stats" />

      {problem ? (
        <p className="notice notice--problem" role="alert" data-testid="journal-problem">
          {problem}
        </p>
      ) : null}

      <div className="panel panel--flush">
        {articles ? (
          <Table
            columns={columns}
            rows={shown}
            rowKey={(article) => article.id}
            label="Articles"
            testId="journal-articles"
            empty={
              <Empty
                icon="journal"
                title={status ? 'No articles in this state' : 'No articles yet'}
                hint={status ? 'Choose another state, or all states.' : 'Start one, and it stays a draft until you publish it.'}
              />
            }
          />
        ) : (
          <p className="loading" role="status">
            Reading the articles…
          </p>
        )}
      </div>
    </section>
  );
}

interface Fields {
  slug: string;
  title: string;
  authorName: string;
  category: string;
  tags: string;
  publishDate: string;
  readingTime: string;
  coverImage: string;
  art: string;
  body: string;
}

const BLANK: Fields = {
  slug: '',
  title: '',
  authorName: 'SFC Editorial',
  category: '',
  tags: '',
  publishDate: '',
  readingTime: '',
  coverImage: '',
  art: '',
  body: '',
};

function fieldsFrom(draft: JournalDraft): Fields {
  return {
    slug: draft.slug,
    title: draft.title,
    authorName: draft.authorName,
    category: draft.category,
    tags: draft.tags.join(', '),
    publishDate: draft.publishDate,
    readingTime: draft.readingTime ?? '',
    coverImage: draft.coverImage ?? '',
    art: draft.art ?? '',
    body: draft.body,
  };
}

/** What the editor may set, in the shape the API takes. */
function bodyOf(fields: Fields) {
  return {
    slug: fields.slug.trim(),
    title: fields.title.trim(),
    authorName: fields.authorName.trim(),
    category: fields.category.trim(),
    tags: fields.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0),
    body: fields.body,
    ...(fields.publishDate ? { publishDate: fields.publishDate } : {}),
    ...(fields.readingTime.trim() ? { readingTime: fields.readingTime.trim() } : {}),
    ...(fields.coverImage.trim() ? { coverImage: fields.coverImage.trim() } : {}),
    ...(fields.art.trim() ? { art: fields.art.trim() } : {}),
  };
}

function ArticleEditor({
  viewer,
  draft,
  onClose,
  onChanged,
}: {
  viewer: Viewer;
  draft: JournalDraft | 'new';
  onClose: () => void;
  onChanged: (draft: JournalDraft) => void;
}): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const mayPublish = viewer.capabilities.includes('journal:publish');

  const [fields, setFields] = useState<Fields>(draft === 'new' ? BLANK : fieldsFrom(draft));
  const [current, setCurrent] = useState<JournalDraft | undefined>(draft === 'new' ? undefined : draft);
  const [revisions, setRevisions] = useState<JournalRevision[]>([]);
  const [preview, setPreview] = useState<PublicJournalPost>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Bumped by anything that appends a version, so the history is re-read rather
  // than left showing the chain as it was when the article was opened.
  const [historyRevision, setHistoryRevision] = useState(0);

  const postId = current?.id;

  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    void platform.journal
      .revisions(organizationId, postId)
      .then((rows) => {
        if (!cancelled) setRevisions(rows);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [organizationId, postId, historyRevision]);

  const set = (name: keyof Fields) => (event: { target: { value: string } }) =>
    setFields((previous) => ({ ...previous, [name]: event.target.value }));

  /** Every action here is the same shape: do it, then hold the answer. */
  async function attempt(work: () => Promise<JournalDraft>): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    setPreview(undefined);
    try {
      const updated = await work();
      setCurrent(updated);
      setFields(fieldsFrom(updated));
      setHistoryRevision((revision) => revision + 1);
      onChanged(updated);
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  const canMove = Boolean(current) && !busy;

  return (
    <section className="card" aria-labelledby="editor-heading">
      <div className="editor-bar">
        <button className="button button--quiet" onClick={onClose}>
          <Icon name="back" size={16} />
          Back to articles
        </button>
        <div className="editor-bar__title">
          <h2 id="editor-heading">{current ? fields.title || 'Untitled article' : 'New article'}</h2>
          <p className="row__sub">
            {current ? (
              <>
                <span className={`pill ${STATUS_TONES[current.status] ?? 'pill--pending'}`}>
                  {STATUS_LABELS[current.status] ?? current.status}
                </span>{' '}
                {current.published ? 'Readers see the published version.' : 'Readers see nothing yet.'}
              </>
            ) : (
              'It stays a draft, and stays private, until you publish it.'
            )}
          </p>
        </div>
      </div>

      {problem ? (
        <p className="notice notice--problem" role="alert" data-testid="article-problem">
          {problem}
        </p>
      ) : null}

      <div className="editor">
        <div className="editor__main">
          <div className="panel">
            <div className="field">
              <label htmlFor="article-title">Title</label>
              <input id="article-title" className="input--headline" value={fields.title} onChange={set('title')} />
            </div>
            <div className="field">
              <label htmlFor="article-body">Body, in markdown</label>
              <textarea id="article-body" className="textarea--prose" rows={18} value={fields.body} onChange={set('body')} />
            </div>
          </div>

          {preview ? (
            <aside className="panel preview" aria-label="Preview">
              <p className="panel__hint">As a reader would see it</p>
              <h3 className="preview__title">{preview.title}</h3>
              <p className="row__sub">
                {[preview.authorName, preview.publishDate, preview.readingTime].filter(Boolean).join(', ')}
              </p>
              <pre className="editor__body">{preview.body}</pre>
            </aside>
          ) : null}
        </div>

        <div className="editor__side">
          <div className="panel">
            <div className="button-stack">
              {current ? (
                <button
                  className="button button--primary"
                  disabled={busy}
                  data-testid="save-article"
                  onClick={() =>
                    void attempt(() =>
                      platform.journal.save(organizationId, current.id, {
                        ...bodyOf(fields),
                        revisionId: current.revisionId,
                      }),
                    )
                  }
                >
                  Save new version
                </button>
              ) : (
                <button
                  className="button button--primary"
                  disabled={busy}
                  data-testid="create-article"
                  onClick={() => void attempt(() => platform.journal.create(organizationId, bodyOf(fields)))}
                >
                  Create draft
                </button>
              )}

              {current ? (
                <button
                  className="button"
                  disabled={busy}
                  data-testid="preview-article"
                  onClick={() => {
                    setProblem(undefined);
                    void platform.journal
                      .preview(organizationId, current.id)
                      .then(setPreview)
                      .catch((error: unknown) => setProblem(describe(error)));
                  }}
                >
                  Preview
                </button>
              ) : null}

              {mayPublish && canMove && current && !current.published ? (
                <button
                  className="button"
                  disabled={busy}
                  data-testid="publish-article"
                  onClick={() => void attempt(() => platform.journal.publish(organizationId, current.id))}
                >
                  Publish
                </button>
              ) : null}

              {mayPublish && canMove && current?.published ? (
                <button
                  className="button"
                  disabled={busy}
                  data-testid="unpublish-article"
                  onClick={() => void attempt(() => platform.journal.unpublish(organizationId, current.id))}
                >
                  Take down
                </button>
              ) : null}

              {mayPublish && canMove && current && current.status !== 'archived' ? (
                <button
                  className="button button--quiet"
                  disabled={busy}
                  data-testid="archive-article"
                  onClick={() => void attempt(() => platform.journal.archive(organizationId, current.id))}
                >
                  Archive
                </button>
              ) : null}

              {current && !current.hasBeenPublished ? (
                <button
                  className="button button--danger"
                  disabled={busy}
                  data-testid="delete-article"
                  onClick={() => {
                    const id = current.id;
                    setBusy(true);
                    setProblem(undefined);
                    void platform.journal
                      .remove(organizationId, id)
                      .then(onClose)
                      .catch((error: unknown) => setProblem(describe(error)))
                      .finally(() => setBusy(false));
                  }}
                >
                  Delete draft
                </button>
              ) : null}
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <h3>Details</h3>
            </div>
            <div className="side-fields">
              <div className="field">
                <label htmlFor="article-slug">Address</label>
                <span className="affix">
                  <span className="affix__prefix">/journal/</span>
                  <input id="article-slug" value={fields.slug} onChange={set('slug')} />
                </span>
              </div>
              <div className="field">
                <label htmlFor="article-author">Author</label>
                <input id="article-author" value={fields.authorName} onChange={set('authorName')} />
              </div>
              <div className="field">
                <label htmlFor="article-category">Category</label>
                <input id="article-category" value={fields.category} onChange={set('category')} />
              </div>
              <div className="field">
                <label htmlFor="article-tags">Tags, comma separated</label>
                <input id="article-tags" value={fields.tags} onChange={set('tags')} />
              </div>
              <div className="field">
                <label htmlFor="article-date">Date a reader sees</label>
                <input id="article-date" type="date" value={fields.publishDate} onChange={set('publishDate')} />
              </div>
              <div className="field">
                <label htmlFor="article-reading">Reading time</label>
                <input id="article-reading" placeholder="6 min read" value={fields.readingTime} onChange={set('readingTime')} />
              </div>
              <div className="field">
                <label htmlFor="article-cover">Cover image</label>
                <input id="article-cover" value={fields.coverImage} onChange={set('coverImage')} />
              </div>
              <div className="field">
                <label htmlFor="article-art">Illustration</label>
                <input id="article-art" value={fields.art} onChange={set('art')} />
              </div>
            </div>
          </div>

          {current && revisions.length > 1 ? (
            <section className="panel" aria-label="Earlier versions">
              <div className="panel__head">
                <h3>Earlier versions</h3>
                <span className="count">{revisions.length - 1}</span>
              </div>
              <ul className="checks checks--compact" data-testid="revision-list">
                {revisions.slice(1).map((revision) => (
                  <li key={revision.id} className="checks__item">
                    <span className="cell-stack">
                      <span>{revision.title}</span>
                      <span className="cell-stack__sub">{dateTime(revision.createdAt)}</span>
                    </span>
                    <button
                      className="button button--small"
                      disabled={busy}
                      aria-label={`Bring back the version from ${new Date(revision.createdAt).toLocaleString()}`}
                      data-testid={`restore-${revision.id.slice(0, 8)}`}
                      onClick={() =>
                        void attempt(() => platform.journal.restore(organizationId, current.id, revision.id))
                      }
                    >
                      Bring back
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function describe(error: unknown): string {
  if (error instanceof PlatformApiError) {
    if (error.status === 403) return 'Your role may not do that.';
    return error.message;
  }
  return 'The articles could not be loaded. Please try again.';
}
